import axios from 'axios';
import { API_URL } from '../config';
import { buildSuperAdminAuthHeaders, getStoredSuperAdminToken } from './authSession';

const BRANDING_CACHE_KEY = 'itraxx_platform_branding_v1';
const BRANDING_CACHE_TS_KEY = 'itraxx_platform_branding_ts_v1';
const BRANDING_CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_BRANDING = {
    platformLogoUrl: null,
    faviconUrl: null,
    dispatchUiMode: 'classic'
};

let brandingRequestInFlight = null;

const normalizeBranding = (data) => ({
    platformLogoUrl: data?.platform_logo_url || data?.logo_url || null,
    faviconUrl: data?.favicon_url || null,
    dispatchUiMode: String(data?.dispatch_ui_mode || 'classic').trim().toLowerCase() || 'classic'
});

const writeCachedPlatformBranding = (branding) => {
    try {
        localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(branding));
        localStorage.setItem(BRANDING_CACHE_TS_KEY, String(Date.now()));
    } catch {
    }
};

export const readCachedPlatformBranding = ({ allowStale = false } = {}) => {
    try {
        const raw = localStorage.getItem(BRANDING_CACHE_KEY);
        const ts = parseInt(localStorage.getItem(BRANDING_CACHE_TS_KEY) || '0', 10);
        if (!raw) return null;
        if (!allowStale && (!ts || Date.now() - ts > BRANDING_CACHE_TTL_MS)) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

export const fetchPlatformBrandingCached = async ({ force = false, timeout = 1500 } = {}) => {
    if (!force) {
        const cached = readCachedPlatformBranding();
        if (cached) {
            return cached;
        }
        if (brandingRequestInFlight) {
            return brandingRequestInFlight;
        }
    }

    const request = (async () => {
        const token = getStoredSuperAdminToken();
        const endpoints = [
            { url: `${API_URL}/public/platform/settings`, headers: undefined },
            token ? { url: `${API_URL}/superadmin/platform/settings`, headers: buildSuperAdminAuthHeaders({}, token) } : null
        ].filter(Boolean);

        let lastError = null;
        for (const endpoint of endpoints) {
            try {
                const response = await axios.get(endpoint.url, { timeout, headers: endpoint.headers });
                const branding = normalizeBranding(response.data || {});
                writeCachedPlatformBranding(branding);
                return branding;
            } catch (error) {
                lastError = error;
                const status = error?.response?.status;
                if (status && status !== 401 && status !== 403 && status !== 404 && status !== 429) {
                    break;
                }
            }
        }

        const error = lastError;
            const staleCached = readCachedPlatformBranding({ allowStale: true });
            if (staleCached) {
                return staleCached;
            }

            if (error?.response?.status === 404 || error?.response?.status === 429) {
                writeCachedPlatformBranding(DEFAULT_BRANDING);
                return DEFAULT_BRANDING;
            }

            throw error;
    })();

    brandingRequestInFlight = request;

    try {
        return await request;
    } finally {
        if (brandingRequestInFlight === request) {
            brandingRequestInFlight = null;
        }
    }
};

export const updatePlatformFavicon = (faviconUrl) => {
    if (!faviconUrl) return;

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = faviconUrl;
};
